import React, { memo, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown, Check, DollarSign, Clock, ArrowUpDown, ArrowUp, ArrowDown, Eye, EyeOff, Receipt, TrendingDown, Percent, Package, Sparkles, Flame, Search, SlidersHorizontal, type LucideIcon } from "lucide-react";
import type { CategoryV2 } from "../services/CategoriesService";
import { useRetailer } from "../hooks/useRetailer";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import ProductFiltersModal from "./ProductFiltersModal";
import LayoutModeMenu from "./LayoutModeMenu";
import { isOnlineOnlyRetailer } from "../config/retailers";

import {
  buildStatusOptions,
  getSortLabel,
  getStatusLabel,
  type StatusOption,
  type StatusCounts,
  type StatusValue,
} from "./ProductsHeader.utils";

interface StoreFilterOption {
  id: string;
  name: string;
  isAll?: boolean;
  isExtra?: boolean;
  distanceMiles?: number;
}

interface ProductsHeaderProps {
  error: string | null;
  productsCount: number;
  onRetry: () => void;
  selectedStoreId: string;
  onStoreChange: (storeId: string) => void;
  storeFilterOptions: StoreFilterOption[];
  /** Extra stores the member hid; listed in the picker's "Hidden stores" row. */
  hiddenExtraStoreOptions?: StoreFilterOption[];
  onHideExtraStore?: (storeId: string) => void;
  onShowExtraStore?: (storeId: string) => void;
  selectedCategories: string[]; // Empty array = all categories
  onCategoriesChange: (categoryIds: string[]) => void;
  categories: (CategoryV2 & { count?: number })[];
  categoriesLoading: boolean;
  showNewestOnly: boolean;
  onShowNewestOnlyChange: (showNewest: boolean) => void;
  showVariantsOnly?: boolean;
  onShowVariantsOnlyChange?: (show: boolean) => void;
  hideCashback?: boolean;
  onHideCashbackChange?: (hide: boolean) => void;
  showHotDeals: boolean;
  setShowHotDeals: (show: boolean) => void;
  showPennyItems: boolean;
  setShowPennyItems: () => void;
  hasOnlineOption?: boolean;
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSortChange?: (sortBy: string) => void;
  onSortDirectionChange?: (direction: "asc" | "desc") => void;
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
  onMinStorePriceChange?: (price: number | undefined) => void;
  onMaxStorePriceChange?: (price: number | undefined) => void;
  onMinMsrpChange?: (price: number | undefined) => void;
  onMaxMsrpChange?: (price: number | undefined) => void;
  onMinDiscountChange?: (discount: number | undefined) => void;
  showInStockOnly?: boolean;
  onShowInStockOnlyChange?: (showInStockOnly: boolean) => void;
  productStatusFilter?: StatusValue;
  onProductStatusFilterChange?: (filter: StatusValue) => void;
  productStatusCounts?: StatusCounts;
  showSaved?: boolean;
  onResetFilters?: () => void;
  viewMode?: "list" | "gallery";
  onViewModeChange?: (mode: "list" | "gallery") => void;
  hiddenProductsCount?: number;
  onManageHiddenProducts?: () => void;
  /** Shows a dot on the filters button when refine filters are active. */
  hasRefineFilters?: boolean;
  /** Rendered at the end of the search row (saved keywords button). */
  searchAccessory?: ReactNode;
}

// Per-field icon for the Sort By sheet rows.
const SORT_OPTION_ICONS: Record<string, LucideIcon> = {
  bestDeal: Flame,
  storePrice: DollarSign,
  msrp: Receipt,
  inventory: Package,
  discountAmount: TrendingDown,
  discountPercentage: Percent,
  openBoxDiscountPercentage: Package,
  lastSeen: Clock,
  lastUpdated: Eye,
  firstAppeared: Sparkles,
};

// Time-based sort fields read "Oldest / Newest" instead of "Low / High".
const TIME_SORT_FIELDS = new Set(["lastUpdated", "lastSeen", "firstAppeared"]);

const ProductsHeader: React.FC<ProductsHeaderProps> = ({
  error,
  productsCount,
  onRetry,
  selectedStoreId,
  onStoreChange,
  storeFilterOptions,
  hiddenExtraStoreOptions,
  onHideExtraStore,
  onShowExtraStore,
  selectedCategories,
  onCategoriesChange,
  categories,
  categoriesLoading,
  showNewestOnly,
  onShowNewestOnlyChange,
  showVariantsOnly = false,
  onShowVariantsOnlyChange,
  hideCashback = true,
  onHideCashbackChange,
  showHotDeals,
  setShowHotDeals,
  showPennyItems,
  setShowPennyItems,
  hasOnlineOption = false,
  searchTerm = "",
  onSearchChange,
  sortBy = "storePrice",
  sortDirection = "asc",
  onSortChange,
  onSortDirectionChange,
  minStorePrice,
  maxStorePrice,
  minMsrp,
  maxMsrp,
  minDiscount,
  onMinStorePriceChange,
  onMaxStorePriceChange,
  onMinMsrpChange,
  onMaxMsrpChange,
  onMinDiscountChange,
  showInStockOnly = true,
  onShowInStockOnlyChange,
  productStatusFilter = "all",
  showSaved = true,
  onProductStatusFilterChange,
  productStatusCounts,
  onResetFilters,
  viewMode = "list",
  onViewModeChange,
  hiddenProductsCount = 0,
  onManageHiddenProducts,
  hasRefineFilters = false,
  searchAccessory,
}) => {
  const retailer = useRetailer();
  const { user } = useUserContext();
  const { t } = useTranslation();
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusButtonRect, setStatusButtonRect] = useState<DOMRect | null>(null);
  const statusButtonRef = useRef<HTMLButtonElement>(null);

  // const hasHomeDepot = user?.retailers?.some((r) => r.startsWith("home_depot"));

  const isBestBuy = retailer === "bestbuy";

  // Inventory is meaningful only for physical-store feeds. Online products
  // use channel availability rather than a tracked on-hand unit count.
  const isOnlineRetailer = isOnlineOnlyRetailer(retailer) || selectedStoreId === "online";

  const sortOptions = [
    { id: "bestDeal", label: t("products.sortBestDeal"), description: t("products.sortBestDealDesc"), icon: "🔥" },
    { id: "storePrice", label: t("products.sortStorePrice"), description: t("products.sortStorePriceDesc"), icon: "💵" },
    { id: "msrp", label: t("products.sortMsrp"), description: t("products.sortMsrpDesc"), icon: "🏷️" },
    ...(!isOnlineRetailer ? [{ id: "inventory", label: t("products.sortInventory"), description: t("products.sortInventoryDesc"), icon: "📦" }] : []),
    { id: "discountAmount", label: t("products.sortDiscountAmount"), description: t("products.sortDiscountAmountDesc"), icon: "💰" },
    { id: "discountPercentage", label: t("products.sortDiscountPercentage"), description: t("products.sortDiscountPercentageDesc"), icon: "📉" },
    ...((retailer === "walmart" || retailer === "dollar_general") && !isOnlineRetailer ? [{ id: "cashbackAmount", label: t("products.sortCashbackAmount"), description: t("products.sortCashbackAmountDesc"), icon: "💸" }] : []),
    ...(isBestBuy ? [{ id: "openBoxDiscountPercentage", label: t("products.sortOpenBoxDiscount"), description: t("products.sortOpenBoxDiscountDesc"), icon: "📦" }] : []),
    { id: "lastSeen", label: t("products.sortChanged"), description: t("products.sortChangedDesc"), icon: "🕒" },
    { id: "lastUpdated", label: t("products.sortUpdated"), description: t("products.sortUpdatedDesc"), icon: "👀" },
    { id: "firstAppeared", label: t("products.sortFirstAppeared"), description: t("products.sortFirstAppearedDesc"), icon: "✨" },
  ];

  // Status options vary by retailer; online feeds omit store-only stock and aisle filters.
  const statusOptions: StatusOption[] = (() => {
    const options = buildStatusOptions({
      retailer,
      isOnlineRetailer,
      counts: productStatusCounts,
      t,
    }).filter((option) => showSaved || option.value !== "saved");
    if (isBestBuy && !options.some((option) => option.value === "open_box")) {
      return [
        ...options,
        { value: "open_box" as const, label: t("products.statusOpenBox"), count: productStatusCounts?.openBox },
      ];
    }
    return options;
  })();
  const statusLabel = getStatusLabel(statusOptions, productStatusFilter);

  const filteredCategories = categories.filter((c) => !/\d/.test(c.rootKey));

  const closeAllDropdowns = () => {
    setIsSortModalOpen(false);
    setIsStatusModalOpen(false);
  };
  // Chip strip overflow: when every chip fits, the strip hugs its content (no
  // fade, no scroll padding) so the row reads as one line, like the app.
  const chipStripRef = useRef<HTMLDivElement>(null);
  const [chipsOverflow, setChipsOverflow] = useState(false);
  useEffect(() => {
    const strip = chipStripRef.current;
    if (!strip || typeof ResizeObserver === "undefined") return;
    const update = () => setChipsOverflow(strip.scrollWidth > strip.clientWidth + 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(strip);
    for (const child of Array.from(strip.children)) observer.observe(child);
    return () => observer.disconnect();
  });
  const hiddenScrollbarStyle = {
    scrollbarWidth: "none" as const,
    msOverflowStyle: "none" as const,
  };

  return (
    <>
      {/* Backdrop to close dropdowns */}
      {(isSortModalOpen || isStatusModalOpen) && (
        <div className="fixed inset-0 z-40" onClick={closeAllDropdowns} />
      )}

      <div className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700 py-1.5 px-2.5 space-y-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="relative min-w-0 flex-1">
            <div
              ref={chipStripRef}
              className={`flex min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap no-scrollbar ${chipsOverflow ? "pr-4" : ""}`}
              style={hiddenScrollbarStyle}
            >
              {/* Store Selector Chip - only for non-online retailers */}
              {!isOnlineRetailer && (
                <button
                  onClick={() => setIsFiltersModalOpen(true)}
                  data-tour="store-chip"
                  className="flex-shrink-0 flex items-center gap-0.5 sm:gap-1 h-8 px-2 sm:px-2.5 hover:bg-purple-200/60 dark:hover:bg-purple-900/50 border rounded-full transition-colors bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800"
                >
                  <span className="text-xs font-semibold text-purple-700 dark:text-purple-400">
                    {selectedStoreId === "all" ? t("products.storesChipLabel") : `#${selectedStoreId}`}
                  </span>
                  <ChevronDown size={12} className="text-purple-600 dark:text-purple-400" />
                </button>
              )}

              {/* Status Filter Chip */}
              {onProductStatusFilterChange && (
                <button
                  ref={statusButtonRef}
                  data-tour="status-chip"
                  onClick={() => {
                    setIsSortModalOpen(false);
                    if (statusButtonRef.current) {
                      setStatusButtonRect(statusButtonRef.current.getBoundingClientRect());
                    }
                    setIsStatusModalOpen(!isStatusModalOpen);
                  }}
                  className="flex-shrink-0 flex items-center gap-1 h-8 px-2 sm:px-2.5 bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700 hover:border-gray-300 dark:hover:border-dark-500 border border-gray-200 dark:border-dark-600 rounded-full transition-colors"
                >
                  <span className="text-xs font-semibold text-gray-700 dark:text-dark-200">{statusLabel}</span>
                  <ChevronDown size={12} className="hidden text-gray-400 sm:block dark:text-dark-400" />
                </button>
              )}

              {/* Sort Chip */}
              {onSortChange && (
                <button
                  data-tour="sort-chip"
                  onClick={() => {
                    setIsStatusModalOpen(false);
                    setIsSortModalOpen(!isSortModalOpen);
                  }}
                  className="flex-shrink-0 flex items-center gap-1 h-8 px-2 sm:px-2.5 bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700 hover:border-gray-300 dark:hover:border-dark-500 border border-gray-200 dark:border-dark-600 rounded-full transition-colors"
                >
                  <span className="text-xs font-semibold text-gray-700 dark:text-dark-200">{getSortLabel(sortBy, t)}</span>
                  <ChevronDown size={12} className="hidden text-gray-400 sm:block dark:text-dark-400" />
                </button>
              )}

              {onManageHiddenProducts && hiddenProductsCount > 0 && (
                <button
                  type="button"
                  onClick={onManageHiddenProducts}
                  className="flex h-8 flex-shrink-0 items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 text-purple-700 transition-colors hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-900/40"
                  title={t("products.manageHiddenProducts")}
                >
                  <EyeOff size={12} aria-hidden="true" />
                  <span className="text-xs font-semibold tabular-nums">{hiddenProductsCount}</span>
                  <span className="sr-only">{t("products.hiddenProductsChip", { count: hiddenProductsCount })}</span>
                </button>
              )}

            </div>
            {chipsOverflow && (
              <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-4 bg-linear-to-l from-white to-transparent dark:from-dark-900" />
            )}
          </div>
          {/* Never shrinks: the chips scroll instead. The divider only shows from sm up, where there's room. */}
          <div className="flex shrink-0 items-center gap-1.5 sm:ml-1 sm:border-l sm:border-gray-200 sm:pl-2 sm:dark:border-dark-700">

            {/* Dollar General's Penny and Coupons live in the feed tabs (Top · All · Penny · Coupons), as in the app. */}
            {/* List / grid, remembered per tab (All and Top). */}
            {onViewModeChange && (
              <LayoutModeMenu
                mode={viewMode === "gallery" ? "grid" : "list"}
                onChange={(mode) => onViewModeChange(mode === "grid" ? "gallery" : "list")}
                compact
              />
            )}

            {/* Refine filters (categories, price, discount, stock) */}
            <button
              type="button"
              onClick={() => setIsFiltersModalOpen(true)}
              data-tour="filters-button"
              aria-label={t("products.moreFilters")}
              title={t("products.moreFilters")}
              className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                hasRefineFilters
                  ? "border-purple-300 bg-purple-100 text-purple-700 hover:bg-purple-200/60 dark:border-purple-700 dark:bg-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-900/50"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-200 dark:hover:border-dark-500 dark:hover:bg-dark-700"
              }`}
            >
              <SlidersHorizontal size={15} aria-hidden="true" />
              {hasRefineFilters && (
                <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-purple-500 ring-2 ring-white dark:ring-dark-900" />
              )}
            </button>
          </div>
        </div>

        {/* Search row: always visible on the All feed; Top deals hide it. */}
        {onSearchChange && (
          <div
            role="search"
            className="flex min-h-9 items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 transition-colors focus-within:border-purple-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-purple-200 dark:border-dark-600 dark:bg-dark-800 dark:focus-within:border-purple-700 dark:focus-within:bg-dark-800 dark:focus-within:ring-purple-900/50"
          >
            <Search size={15} className="flex-none text-gray-400 dark:text-dark-400" aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape" && searchTerm) onSearchChange("");
              }}
              placeholder={t("products.searchTheseDeals")}
              aria-label={t("products.searchFeedA11y")}
              data-tour="search-bar"
              className="min-w-0 flex-1 bg-transparent py-1.5 text-sm font-medium text-gray-950 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-dark-400 [&::-webkit-search-cancel-button]:hidden"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gray-200 text-gray-600 transition hover:bg-gray-300 dark:bg-white/10 dark:text-white/70 dark:hover:bg-white/20"
                aria-label={t("products.clearSearch")}
              >
                <X size={11} />
              </button>
            )}
            {searchAccessory}
          </div>
        )}
      </div>


      {/* Status Dropdown Portal */}
      {isStatusModalOpen && statusButtonRect && onProductStatusFilterChange && createPortal(
        <div
          className="fixed w-56 bg-white dark:bg-dark-800 rounded-xl shadow-xl shadow-gray-900/10 dark:shadow-black/40 border border-gray-100 dark:border-dark-700 overflow-hidden z-[9999]"
          style={{ top: statusButtonRect.bottom + 6, left: statusButtonRect.left }}
        >
          <div className="px-3 py-2 border-b border-gray-100 dark:border-dark-700">
            <span className="text-[10px] font-bold text-gray-400 dark:text-dark-400 uppercase tracking-wider">{t("products.filterByStatus")}</span>
          </div>
          <div className="p-1 max-h-[60vh] overflow-y-auto">
            {statusOptions.map((option) => {
              const isActive = productStatusFilter === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onProductStatusFilterChange(option.value);
                    setIsStatusModalOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors ${
                    isActive
                      ? "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold"
                      : "text-gray-700 dark:text-dark-200 font-medium hover:bg-gray-50 dark:hover:bg-dark-700"
                  }`}
                >
                  <span className="flex-1 text-left truncate">{option.label}</span>
                  {option.count !== undefined && (
                    <span
                      className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${
                        isActive
                          ? "bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300"
                          : "bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400"
                      }`}
                    >
                      {option.count}
                    </span>
                  )}
                  {isActive && <Check size={14} className="flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* Sort Bottom Sheet */}
      {isSortModalOpen && onSortChange && createPortal(
        (() => {
          const isTimeSort = TIME_SORT_FIELDS.has(sortBy);
          const ascLabel = isTimeSort ? t("products.oldestFirst") : t("products.lowToHigh");
          const descLabel = isTimeSort ? t("products.newestFirst") : t("products.highToLow");
          return (
        <div className="fixed inset-0 z-[9999] bg-black/40 dark:bg-black/70 backdrop-blur-[2px] sheet-backdrop-in" onClick={() => setIsSortModalOpen(false)}>
          <div
            className="fixed inset-x-0 bottom-0 bg-white dark:bg-dark-900 rounded-t-3xl w-full max-w-lg mx-auto shadow-2xl border-t border-gray-100 dark:border-dark-700 sheet-panel-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-9 h-1 bg-gray-200 dark:bg-dark-600 rounded-full" />
            </div>
            <div className="px-5 pt-1 pb-3 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-lg tracking-[-0.01em]">{t("products.sortBy")}</h3>
                <p className="text-[12px] text-gray-400 dark:text-dark-400 mt-0.5">
                  {getSortLabel(sortBy, t)}{sortBy === "bestDeal" ? "" : ` · ${sortDirection === "asc" ? ascLabel : descLabel}`}
                </p>
              </div>
              <button onClick={() => setIsSortModalOpen(false)} className="p-2 -mr-1 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-full transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Direction segmented control (Best deal is a ranking, no direction) */}
            {sortBy !== "bestDeal" && <div className="px-5 pb-3">
              <div className="flex bg-gray-100 dark:bg-dark-800 rounded-xl p-1 gap-1">
                <button
                  onClick={() => onSortDirectionChange?.("asc")}
                  aria-label={ascLabel}
                  className={`flex-1 h-10 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    sortDirection === "asc"
                      ? "bg-white dark:bg-dark-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-black/[0.03] dark:ring-white/[0.04]"
                      : "text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300"
                  }`}
                >
                  <ArrowUp className="h-4 w-4" />
                  {ascLabel}
                </button>
                <button
                  onClick={() => onSortDirectionChange?.("desc")}
                  aria-label={descLabel}
                  className={`flex-1 h-10 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    sortDirection === "desc"
                      ? "bg-white dark:bg-dark-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-black/[0.03] dark:ring-white/[0.04]"
                      : "text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300"
                  }`}
                >
                  <ArrowDown className="h-4 w-4" />
                  {descLabel}
                </button>
              </div>
            </div>}

            {/* Sort field label */}
            <div className="px-5 pb-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-500">
                {t("products.sortBy")}
              </span>
            </div>

            <div className="px-3 pb-[max(1rem,env(safe-area-inset-bottom))] max-h-[52vh] overflow-y-auto">
              {sortOptions.map((option) => {
                const isActive = sortBy === option.id;
                const Icon = SORT_OPTION_ICONS[option.id] ?? ArrowUpDown;
                return (
                  <button
                    key={option.id}
                    onClick={() => onSortChange(option.id)}
                    className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-2xl text-left transition-colors ${
                      isActive
                        ? "bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-200 dark:ring-primary-800/50"
                        : "hover:bg-gray-50 dark:hover:bg-dark-800"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      isActive
                        ? "bg-primary-600 text-white shadow-sm shadow-primary-600/25"
                        : "bg-gray-100 dark:bg-dark-800 text-gray-500 dark:text-dark-300"
                    }`}>
                      <Icon className="w-[18px] h-[18px]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`block text-[14px] font-semibold ${
                        isActive ? "text-primary-700 dark:text-primary-200" : "text-gray-900 dark:text-dark-100"
                      }`}>{option.label}</span>
                      <span className={`block text-[12px] mt-0.5 ${
                        isActive ? "text-primary-600/80 dark:text-primary-300/80" : "text-gray-400 dark:text-dark-400"
                      }`}>{option.description}</span>
                    </div>
                    {isActive && (
                      <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                        <Check size={14} className="text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
          );
        })(),
        document.body
      )}

      {/* Product Filters Modal */}
      <ProductFiltersModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        selectedStoreId={selectedStoreId}
        onStoreChange={onStoreChange}
        storeFilterOptions={storeFilterOptions}
        hiddenExtraStoreOptions={hiddenExtraStoreOptions}
        onHideExtraStore={onHideExtraStore}
        onShowExtraStore={onShowExtraStore}
        hasOnlineOption={hasOnlineOption}
        selectedCategories={selectedCategories}
        onCategoriesChange={onCategoriesChange}
        categories={filteredCategories}
        categoriesLoading={categoriesLoading}
        showNewestOnly={showNewestOnly}
        onShowNewestOnlyChange={onShowNewestOnlyChange}
        showVariantsOnly={showVariantsOnly}
        onShowVariantsOnlyChange={
          retailer === "walmart" && user?.admin === true ? onShowVariantsOnlyChange : undefined
        }
        hideCashback={hideCashback}
        onHideCashbackChange={onHideCashbackChange}
        showHotDeals={showHotDeals}
        setShowHotDeals={setShowHotDeals}
        showPennyItems={showPennyItems}
        setShowPennyItems={setShowPennyItems}
        shouldScrollToCategories={false}
        minStorePrice={minStorePrice}
        maxStorePrice={maxStorePrice}
        minMsrp={minMsrp}
        maxMsrp={maxMsrp}
        minDiscount={minDiscount}
        onMinStorePriceChange={onMinStorePriceChange}
        onMaxStorePriceChange={onMaxStorePriceChange}
        onMinMsrpChange={onMinMsrpChange}
        onMaxMsrpChange={onMaxMsrpChange}
        onMinDiscountChange={onMinDiscountChange}
        showInStockOnly={showInStockOnly}
        onShowInStockOnlyChange={onShowInStockOnlyChange}
        onResetFilters={onResetFilters}
      />

      {/* Retry Button */}
      {error && productsCount > 0 && (
        <button
          onClick={onRetry}
          className="fixed bottom-4 right-4 z-30 w-14 h-14 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      )}
    </>
  );
};

export default memo(ProductsHeader);
